<script setup lang="ts">
/**
 * **画布底纹** —— `<Background variant="dots">` 的替代品。
 *
 * ## 为什么不用 SVG pattern
 *
 * 点阵底纹的"数据量"和画布大小无关（永远是那几个点重复），
 * 用 CSS 的 `radial-gradient` + `background-size` 表达最省：
 *
 * ```css
 *   background-image: radial-gradient(circle, <color> <size>px, transparent 0);
 *   background-size: <gap>px <gap>px;
 * ```
 *
 * 而它必须**跟着视口一起平移缩放** —— 否则拖动画布时底纹不动，
 * 看着像"画布没动、节点在飘"。做法是把 `background-position` 绑到
 * 视口偏移、`background-size` 绑到 `gap * zoom`（一行 style，零 DOM 节点）。
 *
 * ⚠️ 底纹**不吃指针事件**：否则框选和右键菜单在空白处全都点不到。
 * `pointer-events: none` 写在 CSS 里。
 */
import { computed } from 'vue'
import type { Viewport } from '../minimapGesture'

const props = withDefaults(
  defineProps<{
    viewport: Viewport
    /** 点的间距（画布单位） */
    gap?: number
    /** 点的半径 */
    size?: number
    color?: string
  }>(),
  { gap: 22, size: 1.4, color: 'var(--np-canvas-grid, rgba(255, 255, 255, 0.11))' },
)

const style = computed(() => {
  const step = Math.max(2, props.gap * props.viewport.zoom)
  return {
    backgroundImage: `radial-gradient(circle, ${props.color} ${props.size}px, transparent 0)`,
    backgroundSize: `${step}px ${step}px`,
    backgroundPosition: `${props.viewport.x}px ${props.viewport.y}px`,
  }
})
</script>

<template>
  <div class="np-graph__background" :style="style" />
</template>
